import { useEffect, useMemo, useRef, useState } from 'react';
import { FontIcon } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { useNavigate } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  PagedDataGrid,
  type DataGridColumn,
  type ICommandBarItem,
  type IPagedDataGridRefType,
} from '@moko/ui';
import ConfirmDialog from '../../common/ConfirmDialog';
import { appRoles } from '../../authConfig';
import { profileDataAtom } from '../../atoms/authAtoms';
import {
  errorMessageAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import {
  deletePricingRate,
  getPricingItems,
  getPricingRates,
  getTimeServices,
  IPricingItem,
  IPricingRate,
} from '../../services/pricingService';
import {
  getBillingClasses,
  getWorkTypes,
} from '../../services/settingsService';
import { conditionLabel, formatRateAmount } from './pricingRateBody';

const PricingRates = () => {
  const navigate = useNavigate();
  const profileData = useAtomValue(profileDataAtom);
  const isAdmin = profileData.roles.includes(appRoles.Admin);
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const ratesRef = useRef<IPagedDataGridRefType>(null);
  const [selectedRate, setSelectedRate] = useState<IPricingRate>();
  const [itemNames, setItemNames] = useState<
    Record<number, { name: string; calculationMethod?: string | null }>
  >({});
  const [workTypeNames, setWorkTypeNames] = useState<Record<number, string>>(
    {}
  );
  const [billingClassNames, setBillingClassNames] = useState<
    Record<number, string>
  >({});
  const [timeServiceNames, setTimeServiceNames] = useState<
    Record<number, string>
  >({});
  const [
    isDeleteRateOpen,
    { setTrue: showDeleteRate, setFalse: hideDeleteRate },
  ] = useBoolean(false);

  useEffect(() => {
    const abortController = new AbortController();
    getPricingItems(abortController, 500, 1)
      .then((page) => {
        const names: Record<
          number,
          { name: string; calculationMethod?: string | null }
        > = {};
        page.items.forEach((item: IPricingItem) => {
          names[item.id] = {
            name: item.name || item.code,
            calculationMethod: item.calculationMethod,
          };
        });
        setItemNames(names);
      })
      .catch(() => undefined);
    getWorkTypes(abortController)
      .then((types) => {
        const names: Record<number, string> = {};
        types.forEach((type) => {
          if (typeof type.id === 'number')
            names[type.id] = type.name || type.code;
        });
        setWorkTypeNames(names);
      })
      .catch(() => undefined);
    getBillingClasses(abortController)
      .then((billingClasses) => {
        const names: Record<number, string> = {};
        billingClasses.forEach((billingClass) => {
          if (typeof billingClass.id === 'number') {
            names[billingClass.id] = billingClass.name || billingClass.code;
          }
        });
        setBillingClassNames(names);
      })
      .catch(() => undefined);
    getTimeServices(abortController, 500, 1)
      .then((page) => {
        const names: Record<number, string> = {};
        page.items.forEach((service) => {
          names[service.id] = service.name || service.code;
        });
        setTimeServiceNames(names);
      })
      .catch(() => undefined);
    return () => abortController.abort();
  }, []);

  const rateColumns: DataGridColumn<IPricingRate>[] = [
    {
      key: 'pricingItemId',
      name: 'Pricing item',
      minWidth: 140,
      width: 220,
      isSortable: true,
      value: (item) =>
        itemNames[item.pricingItemId]?.name ?? String(item.pricingItemId),
    },
    {
      key: 'workTypeId',
      name: 'Work type',
      minWidth: 120,
      width: 150,
      value: (item) => conditionLabel(item.workTypeId, workTypeNames),
    },
    {
      key: 'billingClassId',
      name: 'Billing class',
      minWidth: 120,
      width: 150,
      value: (item) => conditionLabel(item.billingClassId, billingClassNames),
    },
    {
      key: 'timeServiceId',
      name: 'Turnaround',
      minWidth: 120,
      width: 150,
      value: (item) => conditionLabel(item.timeServiceId, timeServiceNames),
    },
    {
      key: 'isDiscountable',
      name: 'Discountable',
      minWidth: 100,
      width: 120,
      value: (item) => (item.isDiscountable ? 'Yes' : 'No'),
    },
    {
      key: 'currencyCode',
      name: 'Currency',
      minWidth: 80,
      width: 110,
      isSortable: true,
    },
    {
      key: 'amount',
      name: 'Amount',
      minWidth: 80,
      width: 140,
      isSortable: true,
      value: (item) =>
        formatRateAmount(
          item.amount,
          item.currencyCode,
          itemNames[item.pricingItemId]?.calculationMethod
        ),
    },
    {
      key: 'effectiveFrom',
      name: 'Effective from',
      minWidth: 140,
      width: 180,
      isSortable: true,
      type: 'date',
    },
    {
      key: 'effectiveTo',
      name: 'Effective to',
      minWidth: 140,
      width: 180,
      type: 'date',
    },
  ];

  const rateCommands: ICommandBarItem[] = useMemo(
    () => [
      {
        key: 'newRate',
        text: 'New',
        icon: <FontIcon iconName="Add" />,
        onClick: () => {
          void navigate('/newPricingRate');
        },
        disabled: !isAdmin,
      },
      {
        key: 'editRate',
        text: 'Edit',
        icon: <FontIcon iconName="Edit" />,
        onClick: () => {
          if (selectedRate)
            void navigate(`/editPricingRate?id=${selectedRate.id}`);
        },
        disabled: !isAdmin || !selectedRate,
      },
      {
        key: 'deleteRate',
        text: 'Delete',
        icon: <FontIcon iconName="Delete" />,
        onClick: () => showDeleteRate(),
        disabled: !isAdmin || !selectedRate,
      },
    ],
    [isAdmin, navigate, selectedRate, showDeleteRate]
  );

  return (
    <>
      <PagedDataGrid<IPricingRate>
        ref={ratesRef}
        columns={rateColumns}
        getAction={getPricingRates}
        commandBarItems={rateCommands}
        selectionMode="single"
        searchable={false}
        onSelectionChange={setSelectedRate}
      />
      <ConfirmDialog
        isModalOpen={isDeleteRateOpen}
        hideModal={hideDeleteRate}
        message={`Pricing rate ${
          selectedRate?.id ?? ''
        } would be deleted, are you sure?`}
        onYesClick={() => {
          hideDeleteRate();
          if (!selectedRate) return;
          const abortController = new AbortController();
          deletePricingRate(abortController, selectedRate.id)
            .then(() => {
              ratesRef.current?.refresh();
              setSuccessMessage(`Pricing rate ${selectedRate.id} deleted.`);
              setSelectedRate(undefined);
            })
            .catch((error: { message?: string }) => {
              setErrorMessage(
                error.message ?? 'Failed to delete pricing rate.'
              );
            });
        }}
      />
    </>
  );
};

export default PricingRates;
